import {
  Component, Input, signal, computed, inject,
  PLATFORM_ID, ChangeDetectionStrategy, HostListener
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-lightbox',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isOpen()) {
      <!-- Backdrop -->
      <div class="fixed inset-0 z-[80] bg-surface-950/95 backdrop-blur-sm animate-[fadeIn_200ms_ease-out]"
           (click)="close()">
      </div>

      <!-- Modal -->
      <div class="fixed inset-0 z-[81] flex items-center justify-center p-4 md:p-8 pointer-events-none">
        <!-- Close -->
        <button (click)="close()" aria-label="Close lightbox"
                class="absolute top-4 right-4 md:top-6 md:right-6 z-[83] w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors pointer-events-auto">
          <svg aria-hidden="true" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Counter -->
        <div class="absolute top-5 left-1/2 -translate-x-1/2 z-[83] text-sm text-white/50 font-medium pointer-events-none">
          {{ activeIndex() + 1 }} / {{ images.length }}
        </div>

        <!-- Prev -->
        @if (images.length > 1) {
          <button (click)="prev(); $event.stopPropagation()" aria-label="Previous image"
                  class="absolute left-3 md:left-6 z-[83] w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all pointer-events-auto hover:scale-110">
            <svg aria-hidden="true" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>

          <!-- Next -->
          <button (click)="next(); $event.stopPropagation()" aria-label="Next image"
                  class="absolute right-3 md:right-6 z-[83] w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all pointer-events-auto hover:scale-110">
            <svg aria-hidden="true" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        }

        <!-- Image -->
        <div class="relative max-w-5xl w-full max-h-[85vh] flex items-center justify-center pointer-events-auto"
             (click)="$event.stopPropagation()">
          <img [src]="currentImage()"
               [alt]="alt + ' - Image ' + (activeIndex() + 1)"
               class="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl select-none transition-opacity duration-300" />
        </div>

        <!-- Thumbnails -->
        @if (images.length > 1) {
          <div class="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-[83] flex items-center gap-2 pointer-events-auto">
            @for (img of images; track $index; let i = $index) {
              <button (click)="goTo(i); $event.stopPropagation()"
                      [attr.aria-label]="'View image ' + (i + 1)"
                      class="w-14 h-10 rounded-md overflow-hidden border-2 transition-all duration-200 hover:opacity-100"
                      [class.border-white]="activeIndex() === i"
                      [class.opacity-100]="activeIndex() === i"
                      [class.border-transparent]="activeIndex() !== i"
                      [class.opacity-50]="activeIndex() !== i">
                <img [src]="img" [alt]="'Thumbnail ' + (i + 1)" class="w-full h-full object-cover" />
              </button>
            }
          </div>
        }
      </div>
    }
  `,
  styles: [`
    :host { display: contents; }
  `],
})
export class LightboxComponent {
  @Input() images: string[] = [];
  @Input() alt = 'Gallery image';

  private platformId = inject(PLATFORM_ID);

  isOpen = signal(false);
  activeIndex = signal(0);

  currentImage = computed(() => this.images[this.activeIndex()] || '');

  open(index: number): void {
    this.activeIndex.set(index);
    this.isOpen.set(true);
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = 'hidden';
    }
  }

  close(): void {
    this.isOpen.set(false);
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = '';
    }
  }

  next(): void {
    this.activeIndex.update(i => (i + 1) % this.images.length);
  }

  prev(): void {
    this.activeIndex.update(i => (i - 1 + this.images.length) % this.images.length);
  }

  goTo(index: number): void {
    this.activeIndex.set(index);
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    if (!this.isOpen()) return;
    switch (e.key) {
      case 'Escape': this.close(); break;
      case 'ArrowRight': this.next(); break;
      case 'ArrowLeft': this.prev(); break;
    }
  }
}
